import React from 'react';
import { Users, Shield, ShieldOff, Lock, Unlock, UserPlus, Edit, Search, Trash2 } from 'lucide-react';
import { supabase, verifyConnection } from '../lib/supabase';
import { NewAdminUserModal } from './NewAdminUserModal';
import { EditAdminUserModal } from './EditAdminUserModal';
import { DeleteUsersModal } from './DeleteUsersModal';
import { getUserRole } from '../lib/auth';

interface User {
  id: string;
  name: string;
  whatsapp: string;
  created_at: string;
  is_blocked: boolean;
  tipo_usuario: string;
}

export function AdminUserList() {
  const [users, setUsers] = React.useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = React.useState<User[]>([]);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [isUpdating, setIsUpdating] = React.useState<string | null>(null);
  const [showNewUserModal, setShowNewUserModal] = React.useState(false);
  const [editingUser, setEditingUser] = React.useState<User | null>(null);
  const [isCurrentUserAdmin, setIsCurrentUserAdmin] = React.useState(false);
  const [retryCount, setRetryCount] = React.useState(0);
  const maxRetries = 3;
  const [selectedUsers, setSelectedUsers] = React.useState<Set<string>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const checkAdminStatus = React.useCallback(async () => {
    try {
      await verifyConnection();

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No session found');
      }

      const { data: isAdmin, error: adminError } = await supabase.rpc('is_admin');
      
      if (adminError) {
        throw adminError;
      }
      
      setIsCurrentUserAdmin(isAdmin);
      return isAdmin;
    } catch (err) {
      console.error('Error checking admin status:', err);
      if (err instanceof Error) {
        if (err.message.includes('Failed to fetch')) {
          setError('Erro de conexão com o servidor. Por favor, verifique sua conexão com a internet e tente novamente.');
        } else if (err.message === 'No session found') {
          setError('Sua sessão expirou. Por favor, faça login novamente.');
          await supabase.auth.signOut();
        } else {
          setError(`Erro ao verificar permissões de administrador: ${err.message}`);
        }
      } else {
        setError('Erro ao verificar permissões de administrador');
      }
      return false;
    }
  }, []);

  const loadUsers = React.useCallback(async () => {
    try {
      const isAdmin = await checkAdminStatus();
      if (!isAdmin) {
        return;
      }

      // Get users with metadata
      const { data: usersData, error: usersError } = await supabase
        .rpc('get_all_users_with_metadata');

      if (usersError) throw usersError;

      // Get block status
      const { data: blockStatus, error: blockError } = await supabase
        .from('user_block_status')
        .select('*');

      if (blockError) throw blockError;

      // Process users data
      const usersWithStatus = usersData.map(user => ({
        id: user.id,
        name: user.name || '',
        whatsapp: user.whatsapp || '',
        created_at: user.created_at,
        is_blocked: blockStatus?.some(status => status.user_id === user.id && status.is_blocked) || false,
        tipo_usuario: user.tipo_usuario || 'cliente'
      }));

      setUsers(usersWithStatus);
      setFilteredUsers(usersWithStatus);
      setError(null);
      setRetryCount(0);
    } catch (err) {
      console.error('Error loading users:', err);
      if (err instanceof Error) {
        if (err.message.includes('Failed to fetch')) {
          if (retryCount < maxRetries) {
            setTimeout(() => {
              setRetryCount(prev => prev + 1);
              loadUsers();
            }, 2000 * (retryCount + 1));
            setError('Tentando reconectar ao servidor...');
          } else {
            setError('Erro de conexão com o servidor. Por favor, verifique sua conexão com a internet e tente novamente.');
          }
        } else {
          setError(`Erro ao carregar usuários: ${err.message}`);
        }
      } else {
        setError('Erro ao carregar usuários');
      }
    } finally {
      setIsLoading(false);
    }
  }, [checkAdminStatus, retryCount]);

  React.useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  React.useEffect(() => {
    const filtered = users.filter(user => 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.whatsapp.includes(searchTerm)
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const handleToggleBlock = async (userId: string, currentStatus: boolean) => {
    try {
      if (!isCurrentUserAdmin) {
        setError('Você não tem permissão para realizar esta ação');
        return;
      }

      setIsUpdating(userId);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setError('Sessão não encontrada');
        return;
      }

      const { error: updateError } = await supabase
        .from('user_block_status')
        .upsert({
          user_id: userId,
          is_blocked: !currentStatus,
          blocked_at: !currentStatus ? new Date().toISOString() : null,
          blocked_by: !currentStatus ? session.user.id : null
        });

      if (updateError) throw updateError;

      await loadUsers();
    } catch (err) {
      console.error('Error toggling block status:', err);
      if (err instanceof Error) {
        setError(`Erro ao atualizar status do usuário: ${err.message}`);
      } else {
        setError('Erro ao atualizar status do usuário');
      }
    } finally {
      setIsUpdating(null);
    }
  };

  const handleDeleteUsers = async () => {
    try {
      setIsDeleting(true);
      
      // Get users to delete
      const usersToDelete = Array.from(selectedUsers);

      // Get user data before deletion for logging
      const { data: userData } = await supabase
        .from('user_profile')
        .select('*')
        .in('id', usersToDelete);
      
      // Call delete_users function
      const { data, error } = await supabase
        .rpc('delete_users', { user_ids: usersToDelete });

      if (error) throw error;

      // Log the deletion
      await supabase.rpc('log_audit_event', {
        p_action_type: 'USER_DELETE',
        p_user_id: (await supabase.auth.getUser()).data.user?.id,
        p_record_id: null,
        p_previous_value: userData,
        p_new_value: null,
        p_additional_info: `Deleted ${usersToDelete.length} users`
      });

      // Check results
      const failures = data.filter(result => !result.success);
      if (failures.length > 0) {
        // Log the error
        await supabase.rpc('log_error', {
          p_error_type: 'DELETE_USERS_ERROR',
          p_error_message: `Failed to delete ${failures.length} users`,
          p_stack_trace: JSON.stringify(failures),
          p_user_id: (await supabase.auth.getUser()).data.user?.id,
          p_url: window.location.href,
          p_request_data: { userIds: usersToDelete },
          p_ip_address: null,
          p_user_agent: navigator.userAgent,
          p_severity: 'HIGH'
        });
        setError(`Erro ao excluir ${failures.length} usuários`);
      }

      // Reload users and reset selection
      await loadUsers();
      setSelectedUsers(new Set());
      setShowDeleteModal(false);
    } catch (err) {
      console.error('Error deleting users:', err);
      // Log the error
      await supabase.rpc('log_error', {
        p_error_type: 'DELETE_USERS_ERROR',
        p_error_message: err instanceof Error ? err.message : 'Unknown error',
        p_stack_trace: err instanceof Error ? err.stack : null,
        p_user_id: (await supabase.auth.getUser()).data.user?.id,
        p_url: window.location.href,
        p_request_data: { userIds: Array.from(selectedUsers) },
        p_ip_address: null,
        p_user_agent: navigator.userAgent,
        p_severity: 'HIGH'
      });
      setError('Erro ao excluir usuários');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSelectUser = (userId: string) => {
    setSelectedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  if (error) {
    return (
      <div className="p-4 bg-red-100 text-red-700 rounded-lg">
        <p className="font-medium">{error}</p>
        <button 
          onClick={() => {
            setError(null);
            setIsLoading(true);
            setRetryCount(0);
            loadUsers();
          }}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  if (isLoading) {
    return <div className="text-center py-4">Carregando...</div>;
  }

  if (!isCurrentUserAdmin) {
    return (
      <div className="p-4 bg-red-100 text-red-700 rounded-lg">
        Você não tem permissão para acessar esta área
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl shadow-lg p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Users className="text-blue-600" size={24} />
            <h2 className="text-xl font-semibold text-gray-900">
              Usuários Cadastrados ({users.length})
            </h2>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => setShowDeleteModal(true)}
              disabled={selectedUsers.size === 0}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full md:w-auto"
            >
              <Trash2 size={20} />
              <span>Excluir Selecionados ({selectedUsers.size})</span>
            </button>
            
            <button
              onClick={() => setShowNewUserModal(true)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors w-full md:w-auto"
            >
              <UserPlus size={20} />
              <span>Novo Usuário</span>
            </button>
          </div>
        </div>

        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Buscar por nome ou WhatsApp..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredUsers.map((user) => (
            <div 
              key={user.id}
              className="bg-gray-50 rounded-lg p-4 space-y-3"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedUsers.has(user.id)}
                    onChange={() => handleSelectUser(user.id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div>
                    <h3 className="font-medium text-gray-900">{user.name}</h3>
                    <p className="text-sm text-gray-500">{user.whatsapp}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                    user.tipo_usuario === 'admin'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {user.tipo_usuario === 'admin' ? (
                      <>
                        <Shield size={14} />
                        Administrador
                      </>
                    ) : (
                      <>
                        <ShieldOff size={14} />
                        Cliente
                      </>
                    )}
                  </span>
                </div>

                <div className="flex items-center justify-end">
                  {user.is_blocked ? (
                    <Lock className="text-red-600" size={20} />
                  ) : (
                    <Unlock className="text-green-600" size={20} />
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                <button
                  onClick={() => setEditingUser(user)}
                  className="flex-1 flex items-center justify-center gap-2 p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <Edit size={18} />
                  <span>Editar</span>
                </button>
                {user.tipo_usuario !== 'admin' && (
                  <button
                    onClick={() => handleToggleBlock(user.id, user.is_blocked)}
                    disabled={isUpdating === user.id}
                    className={`flex-1 flex items-center justify-center gap-2 p-2 rounded-lg transition-colors ${
                      user.is_blocked
                        ? 'text-green-700 hover:bg-green-50'
                        : 'text-red-700 hover:bg-red-50'
                    }`}
                  >
                    {isUpdating === user.id ? (
                      'Atualizando...'
                    ) : user.is_blocked ? (
                      <>
                        <Unlock size={18} />
                        <span>Desbloquear</span>
                      </>
                    ) : (
                      <>
                        <Lock size={18} />
                        <span>Bloquear</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Nenhum usuário encontrado
          </div>
        )}
      </div>

      {showNewUserModal && (
        <NewAdminUserModal
          onClose={() => setShowNewUserModal(false)}
          onSuccess={loadUsers}
        />
      )}

      {editingUser && (
        <EditAdminUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={loadUsers}
        />
      )}
      
      {showDeleteModal && (
        <DeleteUsersModal
          users={users.filter(u => selectedUsers.has(u.id))}
          onConfirm={handleDeleteUsers}
          onCancel={() => setShowDeleteModal(false)}
          isDeleting={isDeleting}
        />
      )}
    </>
  );
}